import type { ResponseReadAll } from "epsm-api-types";
import { useCallback, useState } from "react";
import type { IReadAllParams } from "@/services/RequestParams";

export interface IGetData<T, IOptions> {
	isLoading: boolean;
	error: unknown;
	data: T | null;
	execute: (options: IOptions) => Promise<T | null>;
}

export const concatData = async <
	T,
	TOptions extends { params?: IReadAllParams },
>(
	execute: (options: TOptions) => Promise<ResponseReadAll<T> | null>,
	options: TOptions,
): Promise<T[]> => {
	let hasMore = true;
	let result: T[] = [];
	let offset = 0;
	const PAGE_SIZE = 10000;
	while (hasMore) {
		const data = await execute({
			...options,
			params: {
				...options?.params,
				pagination: {
					limit: PAGE_SIZE,
					offset: offset,
				},
			},
		});
		result = result.concat(data?.data ?? []);
		offset += PAGE_SIZE;
		hasMore = data?.metadata?.pagination?.nextPage ?? false;
	}
	return result;
};

export const useGetData = <T, TOptions>(
	getData: (options: TOptions) => Promise<T>,
): IGetData<T, TOptions> => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<unknown>(null);
	const [data, setData] = useState<T | null>(null);

	const execute = async (options: TOptions): Promise<T | null> => {
		try {
			setIsLoading(true);
			const result = await getData(options);
			setData(result);
			setError(null);
			return result;
		} catch (err) {
			setError(err);
			throw err;
		} finally {
			setIsLoading(false);
		}
	};

	return {
		isLoading,
		error,
		data,
		execute: useCallback(execute, []),
	};
};
